<template>
    <div class="row pt-3" style="border-top: 1px dashed rgba(106, 113, 133, 0.3);">
        <div class="col-sm-6">
            <div class="mb-3">
                <label>
                    <h6 class="f-w-700">Group Name</h6>
                </label>
                <input v-model="compState.name" class="form-control" type="text" placeholder="Name" maxlength="32">
            </div>
        </div>
        <div class=" col-6">
            <label>
                <h6 class="f-w-700">Display Order</h6>
            </label>
            <div class="input-group">
                <input class="form-control" type="number" placeholder="Threshold" v-model="compState.displayOrder">
                <button class="btn btn-outline-secondary rounded-end-0" type="button"
                    @click="displayOrderDecrement">-</button>
                <button class="btn btn-outline-secondary rounded-start-0" type="button"
                    @click="displayOrderIncrement">+</button>
            </div>
        </div>
        <div class=" col-sm-6">
            <div class="row h-100">
                <div class="col h-100">
                    <div class=" checkbox-checked d-flex h-100 align-items-center">
                        <div class="form-check ">
                            <input class="form-check-input" id="show-on-menu" type="checkbox" value=""
                                v-model="compState.showOnMenu">
                            <label class="form-check-label" for="show-on-menu">Show on Menu</label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class=" col-sm-6">
            <div class="row h-100">
                <div class="col h-100">
                    <div class=" checkbox-checked d-flex h-100 align-items-center">
                        <div class="form-check ">
                            <input class="form-check-input" id="used-to-route-orders" type="checkbox" value=""
                                v-model="compState.usedToRouteOrders">
                            <label class="form-check-label" for="used-to-route-orders">Used to Route Orders</label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class=" col-sm-6">
            <div class="row h-100">
                <div class="col h-100">
                    <div class=" checkbox-checked d-flex h-100 align-items-center">
                        <div class="form-check ">
                            <input class="form-check-input" id="service-check" type="checkbox" value=""
                                v-model="compState.nonDiscountable">
                            <label class="form-check-label" for="service-check">Non-Discountable</label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class=" col-sm-6">
            <div class="row h-100">
                <div class="col h-100">
                    <div class=" checkbox-checked d-flex h-100 align-items-center">
                        <div class="form-check ">
                            <input class="form-check-input" id="surchargeable-check" type="checkbox" value=""
                                v-model="compState.nonSurchargeable">
                            <label class="form-check-label" for="surchargeable-check">Non-Surchargeable</label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="col-sm-6">
            <div class="mb-3">
                <label>
                    <h6 class="f-w-700">Alternative Display Language</h6>
                </label>
                <input class="form-control" type="text" placeholder="Alternative Display Language"
                    v-model="compState.altLangText" maxlength="32" readonly data-bs-toggle="modal"
                    data-original-title="test" data-bs-target="#alt-lang-modal">
            </div>
        </div>
    </div>
    <productsGroupsAltLangModal @on-save="onEditAltLang" />
</template>

<script lang="ts" setup>
import { computed, reactive, watch, onMounted, defineAsyncComponent } from 'vue'
import { useProductGroupsStore } from '@/store/management/productGroupsStore';
import type { ProductGroup } from '@/store/management/productGroupsStore';

const productsGroupsAltLangModal = defineAsyncComponent(() => import('@/components/managementPages/productGroupsPages/productGroupsAltLangModal.vue'))

const productGroupsStore = useProductGroupsStore()

onMounted(() => {
    if (tempActiveProductGroup.value?.id) {
        compState.id = tempActiveProductGroup.value.id
        compState.name = tempActiveProductGroup.value.name
        compState.showOnMenu = tempActiveProductGroup.value.showOnMenu
        compState.displayOrder = tempActiveProductGroup.value.displayOrder
        compState.nonDiscountable = tempActiveProductGroup.value.nonDiscountable
        compState.nonSurchargeable = tempActiveProductGroup.value.nonSurchargeable
        compState.usedToRouteOrders = tempActiveProductGroup.value.usedToRouteOrders
        compState.altLangText = altDisplayText(tempActiveProductGroup.value.altDisplayLang)
    }
})

const compState = reactive({
    id: '',
    name: '',
    showOnMenu: false,
    displayOrder: 0,
    nonDiscountable: false,
    nonSurchargeable: false,
    usedToRouteOrders: false,
    altLangText: ''
})


const activeProductGroup = computed(() => {
    return productGroupsStore.getActiveProductGroup
})

const tempActiveProductGroup = computed(() => {
    return productGroupsStore.getTempActiveProductGroup
})

const activeProductGroupId = computed(() => {
    return productGroupsStore.getActiveProductGroupId
})

const altDisplayText = (langList: String[]) => {
    if (langList) {
        return langList.filter(lang => lang !== '').join('|')
    }
    return ''
}

watch(activeProductGroupId, (newVal) => {
    if (newVal) {
        const productGroup = activeProductGroup.value
        compState.id = productGroup.id
        compState.name = productGroup.name
        compState.showOnMenu = productGroup.showOnMenu
        compState.displayOrder = productGroup.displayOrder
        compState.nonDiscountable = productGroup.nonDiscountable
        compState.nonSurchargeable = productGroup.nonSurchargeable
        compState.usedToRouteOrders = productGroup.usedToRouteOrders
        compState.altLangText = altDisplayText(tempActiveProductGroup.value.altDisplayLang)
    }
}, { immediate: true });

watch(compState, (newVal) => {
    productGroupsStore.setTempActiveProductGroup({
        ...tempActiveProductGroup.value,
        id: newVal.id,
        name: newVal.name,
        showOnMenu: newVal.showOnMenu,
        displayOrder: newVal.displayOrder,
        nonDiscountable: newVal.nonDiscountable,
        nonSurchargeable: newVal.nonSurchargeable,
        usedToRouteOrders: newVal.usedToRouteOrders,
    } as ProductGroup)
}, { deep: true });

const displayOrderIncrement = () => {
    compState.displayOrder++
}

const displayOrderDecrement = () => {
    if (compState.displayOrder > 0) {
        compState.displayOrder--
    }
}
const onEditAltLang = () => {
    compState.altLangText = altDisplayText(tempActiveProductGroup.value.altDisplayLang)
}

</script>

<style scoped lang="scss">
.destination-container {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1rem;
}

.hover-pointer {
    cursor: pointer;
}

.nav-link {
    color: var(--bs-heading-color);
}
</style>